import {
  forwardRef, useCallback, useRef, useState,
} from 'react';
import styled from 'styled-components';
import {useFormContext} from 'react-hook-form';
import {ChromePicker} from 'react-color';

import useOnClickOutside from 'utils/useOnClickOutside';

const Container = styled.div`
  position: relative;
  display: flex;
  border-radius: 8px;
  border: ${({theme, $focused}) => theme.input[$focused ? 'focus' : 'default'].border};
  background: ${({theme, $focused}) => theme.input[$focused ? 'focus' : 'default'].bg};
  color: ${({theme, $focused}) => theme.input[$focused ? 'focus' : 'default'].text};
  padding: 8px 16px;
  font-size: 18px;
  line-height: 32px;
  align-items: center;
  justify-content: flex-start;
  cursor: text;
`;

const Icon = styled.div`
  display: flex;
  position: absolute;
  right: 16px;
  top: 14px;
  width: 20px;
  height: 20px;
  border: ${({theme, $focused}) => theme.input[$focused ? 'focus' : 'default'].border};
  background: ${({$color}) => $color};
  border-radius: 4px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const Input = styled.input`
  appearance: none;
  border: none;
  padding: 0 0 0 4px;

  &:focus {
    outline: none;
  }
`;

const Picker = styled.div`
  display: ${({$focused}) => ($focused ? 'block' : 'none')};
  position: absolute;
  z-index: 100;
  top: -250px;
  right: -10px;
  width: 250px;
  height: 270px;
  cursor: default;
`;

const ColorPickerInput = forwardRef(({prefix, name, ...props}, ref) => {
  const {setValue, watch} = useFormContext();
  const containerRef = useRef();
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const focus = useCallback(() => {
    inputRef.current.focus();
  }, []);
  const togglePicker = useCallback((evt) => {
    evt.preventDefault();

    setShowPicker(!showPicker);
  }, [showPicker]);
  const handlePickerChange = useCallback((color) => {
    setValue(name, color.hex.toUpperCase());
  }, [name, setValue]);
  const value = watch(name);

  useOnClickOutside(containerRef, () => setShowPicker(false));

  return (
    <Container ref={containerRef} onClick={focus} $focused={focused}>
      <Input
        name={name}
        type="text"
        {...props}
        onFocus={() => setFocused(true)}
        onBlur={() => !showPicker && setFocused(false)}
        ref={(el) => {
          if (ref) {
            if (typeof ref === 'function') {
              ref(el);
            } else {
              // eslint-disable-next-line no-param-reassign
              ref.current = el;
            }
          }

          inputRef.current = el;
        }}
      />
      <Icon onClick={togglePicker} $color={value} $focused={focused} />
      <Picker $focused={showPicker}>
        <ChromePicker
          disableAlpha
          color={value}
          onChange={handlePickerChange}
        />
      </Picker>
    </Container>
  );
});

export default ColorPickerInput;
